/**
 * Thinning a curve.
 *
 * A recorded pass makes a point every few milliseconds. Most of them say nothing
 * the line through their neighbours does not already say, and they make the lane
 * slow to draw, impossible to select in and unpleasant to edit. Thinning removes
 * the ones that carry no information.
 *
 * The method is Ramer-Douglas-Peucker: find the point furthest from the straight
 * line between the two ends of the run; if it is further than the tolerance,
 * keep it and repeat on each half; if it is not, discard everything between the
 * ends. What survives is the smallest set of points that reproduces the original
 * shape to within the tolerance — which is the right guarantee, because it is
 * about the *curve* rather than about how many points happen to be left.
 *
 * Distance is measured vertically, in the value's own normalised units, not
 * perpendicular to the line. A curve here is a function of time, and what
 * matters is how far the value would be wrong at a given moment; perpendicular
 * distance would mix time and value together and make the tolerance depend on
 * the zoom level of whatever is drawing it.
 */

export interface ThinnablePoint {
  tick: number;
  value: number;
}

/** Below this, thinning would be a no-op that only cost time. */
export const MIN_THIN_TOLERANCE = 0.0005;

/**
 * The indices to keep, given points sorted by tick.
 *
 * Indices rather than points, so a caller holding richer objects — with ids,
 * curve shapes and so on — can keep its own and discard the rest.
 */
export function thinIndices(points: readonly ThinnablePoint[], tolerance: number): number[] {
  if (points.length <= 2) return points.map((_, index) => index);
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;

  // Iterative rather than recursive: a recorded pass can be thousands of points,
  // and a curve that happens to be one long staircase would recurse as deeply.
  const stack: [number, number][] = [[0, points.length - 1]];
  const limit = Math.max(MIN_THIN_TOLERANCE, tolerance);

  while (stack.length > 0) {
    const [from, to] = stack.pop()!;
    if (to <= from + 1) continue;

    const a = points[from];
    const b = points[to];
    const span = b.tick - a.tick;

    let worst = -1;
    let worstIndex = -1;
    for (let i = from + 1; i < to; i++) {
      // Where the straight line between the ends would be at this moment.
      const progress = span === 0 ? 0 : (points[i].tick - a.tick) / span;
      const expected = a.value + (b.value - a.value) * progress;
      const error = Math.abs(points[i].value - expected);
      if (error > worst) {
        worst = error;
        worstIndex = i;
      }
    }

    if (worst > limit && worstIndex >= 0) {
      keep[worstIndex] = 1;
      stack.push([from, worstIndex], [worstIndex, to]);
    }
  }

  const out: number[] = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(i);
  return out;
}

/** The thinned points themselves, for callers that only want the curve. */
export function thinPoints<T extends ThinnablePoint>(points: readonly T[], tolerance: number): T[] {
  const keep = thinIndices(points, tolerance);
  return keep.map((index) => points[index]);
}

/**
 * The largest vertical distance between two curves, sampled at every tick where
 * either has a point. Used to check that thinning kept its promise.
 */
export function maxCurveError(a: readonly ThinnablePoint[], b: readonly ThinnablePoint[]): number {
  if (a.length === 0 || b.length === 0) return a.length === b.length ? 0 : Infinity;
  const ticks = [...new Set([...a.map((p) => p.tick), ...b.map((p) => p.tick)])].sort((x, y) => x - y);
  let worst = 0;
  for (const tick of ticks) worst = Math.max(worst, Math.abs(valueAt(a, tick) - valueAt(b, tick)));
  return worst;
}

/** Linear interpolation along a curve, clamped at both ends. */
export function valueAt(points: readonly ThinnablePoint[], tick: number): number {
  if (points.length === 0) return 0;
  if (tick <= points[0].tick) return points[0].value;
  const last = points[points.length - 1];
  if (tick >= last.tick) return last.value;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (tick > b.tick) continue;
    const span = b.tick - a.tick;
    const progress = span === 0 ? 0 : (tick - a.tick) / span;
    return a.value + (b.value - a.value) * progress;
  }
  return last.value;
}
